'use client'

import { compactClassName } from '@/components/homepage-october/compact'
import { DestinationLink } from './DestinationLink'

import * as React from 'react'
import { ArrowRight, BadgePercent, ChevronDown, X } from '@/components/homepage-october/lucide'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/homepage-october/ui/accordion'
import { Button } from '@/components/homepage-october/ui/button'
import { Img } from './Img'
import { useMedia } from '../hooks'
import type { Model, NavCategory, NavGroup } from '../types'

function allLabel(c: NavCategory) {
  return ({refrigerators:'All refrigerators',freezers:'All freezers',drinks:'All specialty appliances',ice:'All ice makers',brands:'All brands A–Z',parts:'All replacement parts',help:'All free calculators & tools'} as Record<string,string>)[c.id] ?? c.text
}

function GroupLinks({ group }: { group: NavGroup }) {
  return (
    <ul className={compactClassName(group.layout === 'widths' ? 'v3-nav-widths' : 'v3-nav-links')}>
      {group.links.map((l) => {
        const anchor = <DestinationLink href={l.href}>{l.text}{group.layout === 'widths' ? <span className={compactClassName("sr-only")}> wide refrigerators</span> : null}</DestinationLink>
        return <li key={l.href}>{anchor}</li>
      })}
    </ul>
  )
}

function MegaContent({ category, close }: { category: NavCategory; close: () => void }) {
  return (
    <div className={compactClassName("v3-mega-inner")}>
      <div className={compactClassName("v3-mega-heading")}>
        <div><p className={compactClassName("v3-mega-title")}>{category.text}</p><p className={compactClassName("v3-mega-description")}>{category.description}</p></div>
        <DestinationLink href={category.href} className="v3-nav-all">{allLabel(category)}<ArrowRight aria-hidden="true" /></DestinationLink>
        <Button variant="ghost" size="icon" className="size-11 shrink-0 rounded-full!" aria-label={`Close ${category.text} menu`} onClick={close}><X aria-hidden="true" className="size-5" /></Button>
      </div>
      <div data-columns={category.groups.length + Number(Boolean(category.feature))} className={compactClassName(`v3-mega-grid${category.groups.length > 3 ? ' v3-mega-grid-expanded' : ''}`)}>
        {category.groups.map((g) => <div key={g.title} className={compactClassName("v3-nav-group")}><p className={compactClassName("v3-nav-label")}>{g.title}</p><GroupLinks group={g} /></div>)}
        {category.feature ? <DestinationLink className="v3-nav-feature" href={category.feature.href}>
          <div className={compactClassName("v3-nav-feature-image")}><Img src={category.feature.img.src} alt="" /></div>
          <strong>{category.feature.text}</strong><span className={compactClassName("v3-nav-feature-copy")}>{category.feature.description}</span><span className={compactClassName("v3-nav-feature-cta")}>{category.feature.cta}<ArrowRight aria-hidden="true" /></span>
        </DestinationLink> : null}
      </div>
      {category.foot?.length ? <div className={compactClassName("v3-mega-foot")}><span>Helpful shortcuts</span><ul>{category.foot.map((l) => <li key={l.href}><DestinationLink href={l.href}>{l.text}<ArrowRight aria-hidden="true" /></DestinationLink></li>)}</ul></div> : null}
    </div>
  )
}

/** Ordinary disclosure navigation keeps every destination in the HTML without hidden focus proxies. */
export function DesktopNavigation({ model }: { model: Model }) {
  const [open, setOpen] = React.useState('')
  const desktop = useMedia('(min-width: 1024px)')
  const root = React.useRef<HTMLElement>(null)
  const hoverTimer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const active = desktop ? open : ''
  const clearHover = () => clearTimeout(hoverTimer.current)
  const close = (id: string) => {
    clearHover()
    setOpen('')
    root.current?.querySelector<HTMLButtonElement>(`[data-nav-trigger="${id}"]`)?.focus()
  }
  React.useEffect(() => () => clearTimeout(hoverTimer.current), [])
  React.useEffect(() => {
    if (!open) return
    const outside = (event: Event) => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) {
        clearTimeout(hoverTimer.current)
        setOpen('')
      }
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && event.target instanceof Node && root.current?.contains(event.target)) {
        event.preventDefault()
        clearTimeout(hoverTimer.current)
        setOpen('')
        root.current.querySelector<HTMLButtonElement>(`[data-nav-trigger="${open}"]`)?.focus()
      }
    }
    document.addEventListener('pointerdown', outside)
    document.addEventListener('focusin', outside)
    document.addEventListener('keydown', escape)
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('focusin', outside); document.removeEventListener('keydown', escape) }
  }, [open])
  const triggerKey = (event: React.KeyboardEvent<HTMLElement>, category: NavCategory) => {
    if (category.groups.length && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault()
      clearHover()
      setOpen(category.id)
      const last = event.key === 'ArrowUp'
      requestAnimationFrame(() => {
        const links = root.current?.querySelectorAll<HTMLElement>(`#nav-panel-${category.id} a, #nav-panel-${category.id} button`)
        if (links?.length) links[last ? links.length - 1 : 0].focus()
      })
    } else if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
      event.preventDefault()
      const items = [...(root.current?.querySelectorAll<HTMLElement>('[data-slot="navigation-menu-list"] > li > button, [data-slot="navigation-menu-list"] > li > a') ?? [])]
      const index = items.indexOf(event.currentTarget)
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + items.length) % items.length
      setOpen('')
      items[next]?.focus()
    }
  }
  return (
    <nav ref={root} className={compactClassName("v3-primary-nav")} aria-label="Primary shopping navigation">
      <div><ul data-slot="navigation-menu-list" className={compactClassName("flex list-none items-center p-0")}>
        {model.header.mega.map((c) => <li data-slot="navigation-menu-item" key={c.id}
          onPointerEnter={(event) => {
            if (event.pointerType !== 'mouse' || !c.groups.length) return
            clearHover(); hoverTimer.current = setTimeout(() => setOpen(c.id), 180)
          }}
          onPointerLeave={(event) => {
            clearHover()
            if (event.currentTarget.contains(document.activeElement)) return
            hoverTimer.current = setTimeout(() => setOpen((value) => value === c.id ? '' : value), 180)
          }}>
          {c.groups.length ? <>
            <button type="button" id={`nav-trigger-${c.id}`} data-slot="navigation-menu-trigger" data-nav-trigger={c.id}
              data-state={active === c.id ? 'open' : 'closed'} aria-expanded={active === c.id} aria-controls={`nav-panel-${c.id}`}
              onClick={() => { clearHover(); setOpen((value) => value === c.id ? '' : c.id) }}
              onFocus={() => { if (open && open !== c.id) setOpen('') }}
              onKeyDown={(event) => triggerKey(event, c)}>
              {c.text}<ChevronDown aria-hidden="true" className={`size-3 transition-transform ${active === c.id ? 'rotate-180' : ''}`} />
            </button>
            <div id={`nav-panel-${c.id}`} aria-labelledby={`nav-trigger-${c.id}`} role="region" data-state={active === c.id ? 'open' : 'closed'} hidden={active !== c.id} className={compactClassName("v3-mega-panel")}>
              <MegaContent category={c} close={() => close(c.id)} />
            </div>
          </> : <DestinationLink className={c.id === 'deals' ? 'v3-nav-deals' : 'v3-nav-direct'} href={c.href} onFocus={() => setOpen('')} onKeyDown={(event) => triggerKey(event, c)}>{c.id === 'deals' ? <BadgePercent aria-hidden="true" /> : null}{c.text}</DestinationLink>}
        </li>)}
      </ul></div>
    </nav>
  )
}

export function MobileNavigation({ model }: { model: Model }) {
  return (
    <nav aria-label="Mobile shopping navigation" className={compactClassName("v3-mobile-navigation")}>
      <DestinationLink className="v3-mobile-deals" href="/deals"><BadgePercent aria-hidden="true" /><span>Refrigerator & freezer deals<small>Compare price drops across retailers</small></span><ArrowRight aria-hidden="true" /></DestinationLink>
      <Accordion type="single" collapsible>
        {model.header.mega.filter(c => c.id !== 'deals').map((c) => c.groups.length ? <AccordionItem value={c.id} key={c.id}>
          <AccordionTrigger className="v3-mobile-trigger">{c.text}</AccordionTrigger>
          <AccordionContent className="v3-mobile-panel">
            <DestinationLink href={c.href} className="v3-mobile-all">{allLabel(c)}<ArrowRight aria-hidden="true" /></DestinationLink>
            {c.groups.map(g => <div key={g.title} className={compactClassName("v3-mobile-group")}><p className={compactClassName("v3-nav-label")}>{g.title}</p><GroupLinks group={g} /></div>)}
            {c.foot?.length ? <div className={compactClassName("v3-mobile-group")}><p className={compactClassName("v3-nav-label")}>Helpful shortcuts</p><GroupLinks group={{title:'Helpful shortcuts',links:c.foot}} /></div> : null}
          </AccordionContent>
        </AccordionItem> : <DestinationLink key={c.id} href={c.href} className="v3-mobile-direct">{c.text}<ArrowRight aria-hidden="true" /></DestinationLink>)}
      </Accordion>
      <div className={compactClassName("v3-mobile-utility")}><p className={compactClassName("v3-nav-label")}>More from Fridge.com</p><GroupLinks group={{title:'More from Fridge.com',links:model.header.utility}} /></div>
    </nav>
  )
}
